/**
 * MultiLocaleUploadDialog - Reusable dialog for multi-locale upload confirmation
 *
 * Shows when user uploads media and multiple locales are available.
 * Allows user to apply media to all locales or current locale only.
 * Includes "Don't ask again" checkbox for remembering choice.
 *
 * Sprint: multi-locale-upload
 * Phase: 4 - Dialog Implementation (TDD GREEN)
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
} from '@mui/material';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Props for MultiLocaleUploadDialog
 */
export interface MultiLocaleUploadDialogProps {
  /** Whether dialog is open */
  open: boolean;

  /** Callback when dialog is closed (cancel) */
  onClose: () => void;

  /** Type of media being uploaded */
  mediaType: 'image' | 'video';

  /** Current editing locale */
  currentLocale: string;

  /** All available locales */
  availableLocales: string[];

  /**
   * Callback when user clicks "Add for all locales"
   * @param rememberChoice - true if "Don't ask again" is checked
   */
  onApplyAll: (rememberChoice: boolean) => void;

  /**
   * Callback when user clicks "Keep only for {locale}"
   * @param rememberChoice - true if "Don't ask again" is checked
   */
  onApplyCurrent: (rememberChoice: boolean) => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * MultiLocaleUploadDialog - Confirmation dialog for multi-locale upload
 *
 * Layout:
 * ┌────────────────────────────────────────────┐
 * │ Apply to Multiple Locales?                 │
 * ├────────────────────────────────────────────┤
 * │ You have 3 locales configured.             │
 * │ Would you like to apply this image to all  │
 * │ locales?                                   │
 * │                                            │
 * │ Current: en-US                             │
 * │ All: en-US, ru-RU, de-DE                   │
 * │                                            │
 * │ [✓] Don't ask again                        │
 * ├────────────────────────────────────────────┤
 * │ [Cancel] [Keep only for en-US] [Add all]   │
 * └────────────────────────────────────────────┘
 */
export const MultiLocaleUploadDialog: React.FC<MultiLocaleUploadDialogProps> = ({
  open,
  onClose,
  mediaType,
  currentLocale,
  availableLocales,
  onApplyAll,
  onApplyCurrent,
}) => {
  // State for "Don't ask again" checkbox
  const [rememberChoice, setRememberChoice] = useState(false);

  // Reset checkbox state when dialog opens/closes
  React.useEffect(() => {
    if (!open) {
      setRememberChoice(false);
    }
  }, [open]);

  // Handle checkbox toggle
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRememberChoice(event.target.checked);
  };

  // Handle "Keep only for current" click
  const handleApplyCurrent = () => {
    onApplyCurrent(rememberChoice);
  };

  // Handle "Add for all locales" click
  const handleApplyAll = () => {
    onApplyAll(rememberChoice);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      data-testid="multi-locale-upload-dialog"
    >
      <DialogTitle>Apply to Multiple Locales?</DialogTitle>

      <DialogContent>
        {/* Description */}
        <Typography sx={{ mb: 2 }}>
          You have {availableLocales.length} locales configured.
          Would you like to apply this {mediaType} to all locales?
        </Typography>

        {/* Locale info */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Current: <strong>{currentLocale}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All: {availableLocales.join(', ')}
          </Typography>
        </Box>

        {/* Don't ask again checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              checked={rememberChoice}
              onChange={handleCheckboxChange}
              data-testid="dont-ask-again-checkbox"
            />
          }
          label="Don't ask again"
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleApplyCurrent}>
          Keep only for {currentLocale}
        </Button>
        <Button
          onClick={handleApplyAll}
          variant="contained"
          data-testid="apply-all-locales-button"
        >
          Add for all locales
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MultiLocaleUploadDialog;
